import React from "react";
import { TFunction } from "i18next";
import dateFormatter from "../util/dateFormatter";
import {
  MeetingItem,
  MeetingItemOverdueOptions,
  MeetingItemPriorityOptions,
  MeetingItemStatus,
  RiskIndicator,
} from "./meetingItem";
import { GridColDef } from "@mui/x-data-grid";
import { t } from "i18next";
export interface MeetingItemRepresentation {
  id: string;
  content: string;
  status: MeetingItemStatus;
  risk: RiskIndicator;
  sessionId?: string;
  sessionName?: string;
  clientId?: string;
  isCoachingTask?: boolean;
  task: boolean;
  decision: boolean;
  info: boolean;
  index?: number;
  identifier?: string;
  meetingId?: string;
  meetingName?: string;
  meetingDate?: Date;
  meetingAttachments?: number;
  attachments?: number;
  description?: string;
  creator?: string;
  isSnapshot?: boolean;
  snapshotFor?: string;
  actor?: string;
  category?: string;
  dueDate?: Date;
  previousDueDates?: Date[];
  overdue: boolean;
  completedDate?: Date;
  project?: string;
  tags?: string[];
  comments?: string;
  data?: MeetingItem;
  archived?: boolean;
  prioritized?: boolean;
  External?: boolean;
}

export const hasRepresentationChanged = (
  item: MeetingItemRepresentation,
): boolean => {
  const itemToSave = getMeetingItemFromRepresentation(item);
  if (item.dueDate) {
    item.task = true;
  }
  if (item.data && JSON.stringify(item.data) === JSON.stringify(itemToSave)) {
    return false;
  }
  return true;
};

export const isStatusUpdated = (item: MeetingItemRepresentation): boolean => {
  return item.data?.status !== item.status;
};

export const emptyItemRepresentation = {
  id: "1111",
  content: "",
  status: MeetingItemStatus.NotStarted,
  task: true,
  decision: false,
  info: false,
};

export const priorityKeys = Object.values(MeetingItemPriorityOptions);
export const overdueKeys = Object.values(MeetingItemOverdueOptions);

export const statusKeys = Object.values(MeetingItemStatus);

export const riskKeys = Object.values(RiskIndicator);

// ["notstarted", "inprogress", "completed", "cancelled", "onhold"]

export const priorityTranslatedLabelOptions = (t: TFunction) =>
  priorityKeys.map((key) => {
    const isHighPriority = key !== "high_priority";
    const normalized = isHighPriority ? "high_priority" : "normal_priority";

    return {
      value: isHighPriority,
      label: t(`item_priority.${normalized}`),
    };
  });

export const priorityToTextConverter = (value: boolean) =>
  value ? t("item_priority.high_priority") : t("item_priority.normal_priority");
export const overdueToTextConverter = (value: boolean) =>
  value ? t("item_overdue.delayed") : t("item_overdue.on_track");
export const externalToTextConverter = (value: boolean) =>
  value ? "External" : "Internal";

export const statusTranslatedLabelOptions = (t: TFunction) =>
  statusKeys.map((key) => {
    return {
      value: key,
      label: t(`item_status.${key}`),
    };
  });

export const overdueTranslatedLabelOptions = (t: TFunction) =>
  overdueKeys.map((key) => {
    const isOverdue = key === "delayed";
    const normalized = isOverdue ? "delayed" : "on_track";
    return {
      value: !isOverdue,
      label: t(`item_overdue.${normalized}`),
    };
  });

export const riskTranslatedLabelOptions = (t: TFunction) =>
  riskKeys.map((key) => {
    return {
      value: key,
      label: t(`item_risk.${key}`),
    };
  });

export const externalTranslatedLabelOptions = (t: TFunction) => [
  {
    value: true,
    label: "External",
  },
  {
    value: false,
    label: "Internal",
  },
];

export const convertToRepresentation = (
  data: MeetingItem[] | undefined,
): MeetingItemRepresentation[] => {
  const mapped = (data || []).map((meetingItem) => {
    // Determine if actor email is external based on creator domain
    const actorEmail = meetingItem.actor;
    const creatorEmail = meetingItem.creator;

    // Get the internal domain from the creator's email
    const internalDomain =
      creatorEmail && creatorEmail.includes("@")
        ? creatorEmail.split("@")[1]
        : undefined;

    // Check if actor is external compared to the creator's domain (internal domain)
    const isExternal = isExternalEmail(actorEmail, internalDomain);

    return {
      ...meetingItem,
      task: meetingItem?.types?.includes("task"),
      decision: meetingItem?.types?.includes("decision"),
      info: meetingItem?.types?.includes("info"),
      External: isExternal,
      data: meetingItem,
    } as MeetingItemRepresentation;
  });
  return mapped;
};

export const convertToRepresentationSortedByCreateDateAscending = (
  data: MeetingItem[] | undefined,
): MeetingItemRepresentation[] => {
  return convertToRepresentation(data).sort((a, b) => {
    if (a.data?.createdDate < b.data?.createdDate) return -1;
    if (a.data?.createdDate > b.data?.createdDate) return 1;
    return 0;
  });
};

export const getMeetingItemFromRepresentation = (
  item: MeetingItemRepresentation,
): MeetingItem => {
  if (!item) {
    return null;
  }
  const types = [];
  if (item.task) {
    types.push("task");
  }
  if (item.decision) {
    types.push("decision");
  }
  if (item.info) {
    types.push("info");
  }
  const { data, info, task, decision, ...itemWithoutData } = item;
  return {
    ...itemWithoutData,
    // Preserve explicit clears: serialize as `null` so axios/JSON keeps the
    // key and the backend can $set the field (undefined would drop the key
    // and Mongoose would skip the update, leaving the old value in DB).
    dueDate: itemWithoutData.dueDate
      ? new Date(itemWithoutData.dueDate)
      : (null as unknown as Date),
    meetingId: data?.meetingId || "will be updated by parent components",
    id: data?.id,
    types,
  };
};
//columns for the top row - adding a new meeting item
export const addIcon: GridColDef = {
  field: "addIcon",
  editable: false,
  width: 60,
  display: "flex",
};
export const identifierColumn: GridColDef = {
  field: "identifier",
  headerName: "item.identifier",
  editable: false,
  width: 60,
  hideable: false,
};
export const indexColumn: GridColDef = {
  field: "index",
  headerName: "item.identifier",
  width: 50,
  editable: false,
  hideable: false,
  display: "flex",
};
export const meetingNameColumn: GridColDef = {
  field: "meetingName",
  headerName: "item.meetingName",
  editable: false,
};
export const creatorColumn: GridColDef = {
  field: "creator",
  headerName: "item.creator",
  editable: false,
};
export const categoryColumn: GridColDef = {
  field: "category",
  headerName: "item.category",
  editable: false,
};
export const contentColumn: GridColDef = {
  field: "content",
  headerName: "item.content",
  editable: false,
  width: 270,
};
export const taskColumn = {
  field: "taskType",
  headerName: "item.taskType",
  width: 70,
  editable: false,
  align: "center",
  headerAlign: "center",
};
export const typesColumn = {
  field: "types",
  headerName: "item.types",
  minWidth: 140,
  editable: false,
  align: "center",
  headerAlign: "center",
};

export const overdueColumn = {
  field: "overdue",
  headerName: "item.overdue",
  minWidth: 50,
  editable: false,
  align: "center",
  display: "flex",
  headerAlign: "center",
};

export const prioritizedColumn = {
  field: "prioritized",
  headerName: "item.prioritized",
  minWidth: 50,
  editable: false,
  align: "center",
  display: "flex",
  headerAlign: "center",
};

export const decisionColumn = {
  field: "decisionType",
  headerName: "item.decisionType",
  width: 70,
  editable: false,
  align: "center",
  headerAlign: "center",
};

export const informationColumn = {
  field: "informationType",
  headerName: "item.informationType",
  width: 70,
  editable: false,
  align: "center",
  headerAlign: "center",
};

export const actorColumn: GridColDef = {
  field: "actor",
  headerName: "item.actor",
  editable: false,
  display: "flex",
  valueSetter: (value, row) => {
    if (value) {
      if (!row.task) row.task = true;
      return row;
    }
    return row;
  },
};

// Converts a YYYY-MM-DD ISO date string into the Date shape the rest of the
// app stores on `dueDate` — noon in the local timezone, shifted so the wall
// clock matches after the UTC round-trip. Mirrors dueDateColumn.valueSetter.
// Parses the ISO as *local* components so that selecting 2026-04-23 stores
// April 23 regardless of the viewer's timezone (new Date("2026-04-23") would
// parse as UTC midnight and roll back a day west of UTC).
export const isoStringToDueDate = (
  iso: string | null | undefined,
): Date | undefined => {
  if (!iso) return undefined;
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  const localDate = new Date(year, month - 1, day, 12, 0, 0, 0);
  const utcTime = localDate.getTime() - localDate.getTimezoneOffset() * 60000;
  return new Date(utcTime);
};

export const dueDateColumn: GridColDef = {
  field: "dueDate",
  headerName: "item.dueDate",
  type: "date" as const,
  width: 110,
  editable: true,
  valueSetter: (value, row) => {
    if (value) {
      const localDate = new Date(value);
      localDate.setHours(12, 0, 0, 0);
      const utcTime =
        localDate.getTime() - localDate.getTimezoneOffset() * 60000;
      row.dueDate = new Date(utcTime);

      if (!row.task) row.task = true;
      return row;
    }
    return row;
  },
  valueFormatter: (value) =>
    value ? dateFormatter.format(new Date(value)) : t("item.notAvailable"),
};

export const riskColumn = {
  field: "risk",
  headerName: "item.risk",
  renderHeader: () => "",
  editable: true,
  width: 10,
};
export const getRiskColor = (risk: RiskIndicator): string => {
  switch (true) {
    case risk === RiskIndicator.Roadblock:
      return "#DA1E28";
    case risk === RiskIndicator.AtRisk:
      return "#FF832B";
    case risk === RiskIndicator.OnTrack:
      return "#108B38";
    default:
      return "transparent";
  }
};

export const createdDateColumn = {
  field: "createdDate",
  headerName: "item.createdDate",
  type: "date" as const,
  editable: false,
  width: 130,
  valueFormatter: (value) =>
    value ? dateFormatter.format(new Date(value)) : t("item.notAvailable"),
};

export const statusColumn: GridColDef<any> = {
  field: "status",
  headerName: "item.status",
  minWidth: 185,
  maxWidth: 185,
  editable: true,
};

export const tagsColumn: GridColDef = {
  field: "tags",
  headerName: "item.tags",
  width: 200,
  editable: true,
  valueSetter: (value, row) => {
    if (value) {
      row.tags = value;
      return row;
    }
    return row;
  },
  display: "flex",
};

// Helper function to determine if an email is external
export const isExternalEmail = (
  email?: string,
  internalDomain?: string,
): boolean => {
  // If no email or no internal domain, treat as internal
  if (!email || !internalDomain) return false;

  // If email doesn't contain '@', it's just a name - treat as internal
  if (!email.includes("@")) return false;

  const emailDomain = email.toLowerCase().split("@")[1];
  const internalDomainClean = internalDomain.toLowerCase().replace("@", "");

  return (
    emailDomain && internalDomainClean && emailDomain !== internalDomainClean
  );
};

// Helper function to determine if an email is external compared to a reference email
export const isExternalEmailFromReference = (
  email?: string,
  referenceEmail?: string,
): boolean => {
  // If no email or no reference email, treat as internal
  if (!email || !referenceEmail) return false;

  // If email doesn't contain '@', it's just a name - treat as internal
  if (!email.includes("@")) return false;

  // If reference email doesn't contain '@', treat as internal
  if (!referenceEmail.includes("@")) return false;

  const emailDomain = email.toLowerCase().split("@")[1];
  const referenceDomain = referenceEmail.toLowerCase().split("@")[1];

  return emailDomain && referenceDomain && emailDomain !== referenceDomain;
};

// External column definition - works like risk column
export const externalEmailMarkerColumn: GridColDef = {
  field: "External",
  headerName: "",
  width: 40,
  sortable: false,
  filterable: true,
  disableColumnMenu: false,
  editable: false,
  align: "center",
  headerAlign: "center",
  type: "singleSelect",
  valueOptions: [
    { value: true, label: "true" },
    { value: false, label: "false" },
  ],
};
